import React, { Component, Fragment, useEffect, useState, useRef } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import _ from 'lodash';
import Swal from 'sweetalert2';
import { getCASASTesting, saveCASASTesting } from '../../../Services/CASASTestingService';
import { utils } from '../../../helpers/utils';
import moment from 'moment';

export type CASASTestingValues = {
    [key: string]: any;
}

export type CASASTestingRef = {
    [key: string]: React.RefObject<HTMLElement | null>
}

export type CASASTestingErrors = {
    [key: string]: string;
}

export type CASASTestingTouched = {
    [key: string]: boolean;
}

export type ValidtionFunctions = {
    [key: string]: Function;
}

export type CASASTestingMasterData = {
    casastestingId: number;
    firstname: string
    lastname: string
    email: string
    phoneNumber: string
    casastestingCompleted: boolean;
    noShowForTesting: boolean;
    counselorComments: string;
    staffComments: string;
    counselorName: string;
    testingCompletedDate?: string | null;
    registeredTime?: string| null;
}

export function usePublicCASASTestingForm() {
    const history = useHistory();

    let { TId } = useParams<{ TId: string }>();

    useEffect(() => {
        fetchData(TId);
    }, []);

    async function fetchData(TId: string) {
        try {
            //get CASAS testing data by presentationId
            if (TId != '0') {
                const id: number = parseInt(TId);
                getCASASTesting(id, false).then((response) => {
                    if (response && response.status === 200 && response.data) {
                        let data = response.data as CASASTestingMasterData;
                        let newValues = {
                            firstName: data.firstname,
                            lastName: data.lastname,
                            phoneNumber: utils.formatPhoneNumber(data.phoneNumber),
                            email: data.email,
                            casastestingCompleted: data.casastestingCompleted,
                            noShowForTesting: data.noShowForTesting,
                            counselorComments : data.counselorComments,
                            staffComments: data.staffComments,
                            counselorName: data.counselorName,
                            testingCompletedDate: data.testingCompletedDate ? moment(data.testingCompletedDate).format('yyyy-MM-DD') : null,
                            registeredTime: data.registeredTime ? moment(data.registeredTime).format('MM/DD/YYYY') : null,
                        };
                        setValues(newValues);
                    } else {
                        throw new Error('Unable to fetch Employer Spotlight Presentation data!');
                    }

                })
            }

        } catch (error) {
            Swal.fire(
                {
                    icon: 'error',
                    title: 'Error While Processing',
                    text: `Unable to get data for the form.`,
                    showConfirmButton: true,
                    confirmButtonText: "OK",
                    confirmButtonColor: '#1678c2'
                }
            ).then(() => {
            });
        }

    }

    const defaultValues: CASASTestingValues = {
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        confirmEmail: '',
        casastestingCompleted: false,
        noShowForTesting: false,
        counselorComments: '',
        staffComments:'',
        testingCompletedDate: null,
        registeredTime: null,
    };
    const validations = {

        firstName: async function (values: CASASTestingValues) {
            // Do validation here
            // two promises, one for local, one for async, usually serverside.
            let error: string | null = null;
            let promises = [
                new Promise((resolve, reject) => {
                    // local validation logic here.
                    if (TId == '0' &&!values.firstName) {
                        error = 'Please enter first name';
                    }
                    if (error != null) {
                        reject(error);
                    } else {
                        resolve(null);
                    }
                }),
                new Promise(
                    (resolve, reject) => {
                        // do server validation logic here.
                        resolve(null);
                    }
                ),
            ];
            let result = await Promise.all(promises);
            return result;
        },
        lastName: async function (values: CASASTestingValues) {
            // Do validation here
            // two promises, one for local, one for async, usually serverside.
            let error: string | null = null;
            let promises = [
                new Promise((resolve, reject) => {
                    // local validation logic here.
                    if (TId == '0' && !values.lastName) {
                        error = 'Please enter last name';
                    }
                    if (error != null) {
                        reject(error);
                    } else {
                        resolve(null);
                    }
                }),
                new Promise(
                    (resolve, reject) => {
                        // do server validation logic here.
                        resolve(null);
                    }
                ),
            ];
            let result = await Promise.all(promises);
            return result;
        },
        phoneNumber: async function (values: CASASTestingValues) {
            // Do validation here
            // two promises, one for local, one for async, usually serverside.
            let error: string | null = null;
            let promises = [
                new Promise((resolve, reject) => {
                    // local validation logic here.
                    
                    if (TId == '0' && values.phoneNumber && !isValidPhone(values.phoneNumber)) {
                        error = 'Please enter a valid phone number: (123) 456-7890'
                    }
                    if (error != null) {
                        reject(error);
                    } else {
                        resolve(null);
                    }
                }),
                new Promise(
                    (resolve, reject) => {
                        // do server validation logic here.
                        resolve(null);
                    }
                ),
            ];
            let result = await Promise.all(promises);
            return result;
        },
        email: async function (values: CASASTestingValues) {
            // Do validation here
            // two promises, one for local, one for async, usually serverside.
            let error: string | null = null;
            let promises = [
                new Promise((resolve, reject) => {
                    // local validation logic here.
                    if (TId == '0' && !(values.email && isValidEmail(values.email) === true)) {
                        error = 'Please provide a valid email'
                    }
                    if (error != null) {
                        reject(error);
                    } else {
                        resolve(null);
                    }
                }),
                new Promise(
                    (resolve, reject) => {
                        // do server validation logic here.
                        resolve(null);
                    }
                ),
            ];
            let result = await Promise.all(promises);
            return result;
        },
        confirmEmail: async function (values: CASASTestingValues) {
            // Do validation here
            // two promises, one for local, one for async, usually serverside.
            let error: string | null = null;
            let promises = [
                new Promise((resolve, reject) => {
                    // local validation logic here.
                    if (TId == '0') {
                        if (!(values.confirmEmail && isValidEmail(values.confirmEmail) === true)) {
                            error = 'Please provide a valid email'
                        } else if (values.confirmEmail !== values.email) {
                            error = 'Email entries does not match'
                        }
                    }
                    if (error != null) {
                        reject(error);
                    } else {
                        resolve(null);
                    }
                }),
                new Promise(
                    (resolve, reject) => {
                        // do server validation logic here.
                        resolve(null);
                    }
                ),
            ];
            let result = await Promise.all(promises);
            return result;
        },

        casastestingCompleted: async function (values: CASASTestingValues) {
            // Do validation here
            // two promises, one for local, one for async, usually serverside.
            let error: string | null = null;
            let promises = [
                new Promise((resolve, reject) => {
                    // local validation logic here.
                    if (error != null) {
                        reject(error);
                    } else {
                        resolve(null);
                    }
                }),
                new Promise(
                    (resolve, reject) => {
                        // do server validation logic here.
                        resolve(null);
                    }
                ),
            ];
            let result = await Promise.all(promises);
            return result;
        },
        noShowForTesting: async function (values: CASASTestingValues) {
            // Do validation here
            // two promises, one for local, one for async, usually serverside.
            let error: string | null = null;
            let promises = [
                new Promise((resolve, reject) => {
                    // local validation logic here.
                    if (error != null) {
                        reject(error);
                    } else {
                        resolve(null);
                    }
                }),
                new Promise(
                    (resolve, reject) => {
                        // do server validation logic here.
                        resolve(null);
                    }
                ),
            ];
            let result = await Promise.all(promises);
            return result;
        },
        counselorComments: async function (values: CASASTestingValues) {
            // Do validation here
            // two promises, one for local, one for async, usually serverside.
            let error: string | null = null;
            let promises = [
                new Promise((resolve, reject) => {
                    // local validation logic here.
                    if (error != null) {
                        reject(error);
                    } else {
                        resolve(null);
                    }
                }),
                new Promise(
                    (resolve, reject) => {
                        // do server validation logic here.
                        resolve(null);
                    }
                ),
            ];
            let result = await Promise.all(promises);
            return result;
        },
        staffComments: async function (values: CASASTestingValues) {
            // Do validation here
            // two promises, one for local, one for async, usually serverside.
            let error: string | null = null;
            let promises = [
                new Promise((resolve, reject) => {
                    // local validation logic here.
                    if (error != null) {
                        reject(error);
                    } else {
                        resolve(null);
                    }
                }),
                new Promise(
                    (resolve, reject) => {
                        // do server validation logic here.
                        resolve(null);
                    }
                ),
            ];
            let result = await Promise.all(promises);
            return result;
        },
        testingCompletedDate: async function (values: CASASTestingValues) {
            // Do validation here
            // two promises, one for local, one for async, usually serverside.
            let error: string | null = null;
            let promises = [
                new Promise((resolve, reject) => {
                    // local validation logic here.
                    if(values.casastestingCompleted === true){
                        if (!values.testingCompletedDate) {
                            error = 'Please enter Testing Completed Date';
                        } else if (!_isValidDate(values.testingCompletedDate)) {
                            error = 'Please enter a valid date, format like "mm/dd/yyyy"';
                        }
                    }
                    if (error != null) {
                        reject(error);
                    } else {
                        resolve(null);
                    }
                }),
                new Promise(
                    (resolve, reject) => {
                        // do server validation logic here.
                        resolve(null);
                    }
                ),
            ];
            let result = await Promise.all(promises);
            return result;
        },
        registeredTime: async function (values: CASASTestingValues) {
            // Do validation here
            // two promises, one for local, one for async, usually serverside.
            let error: string | null = null;
            let promises = [
                new Promise((resolve, reject) => {
                    // local validation logic here.
                    if (error != null) {
                        reject(error);
                    } else {
                        resolve(null);
                    }
                }),
                new Promise(
                    (resolve, reject) => {
                        // do server validation logic here.
                        resolve(null);
                    }
                ),
            ];
            let result = await Promise.all(promises);
            return result;
        },
    }

    let isSubmit = false;
    const debounceTime = 250;
    const ref = useRef(initRefs(defaultValues));
    const [values, setValues] = useState<CASASTestingValues>(defaultValues);
    const [errors, setErrors] = useState<CASASTestingErrors>({});
    const [touched, setTouched] = useState<CASASTestingTouched>({});
    //const debouncedValidateForm = _.debounce(validateForm, debounceTime);
    const debouncedValidateForm = validateForm;
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [masterData, setMasterData] = useState<CASASTestingMasterData | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);

    let validationCount = 0;
    let validationTotal = 1;

    function initRefs(viewModel: CASASTestingValues) {
        if (viewModel == null) {
            return {};
        } else {
            let result: CASASTestingRef = {};
            Object.keys(viewModel).forEach((key) => {
                result[key] = React.createRef<HTMLElement | null>();
            });
            return result;
        }
    }

    function registerRef(ref: HTMLElement | null) {
        console.log(ref);
        console.log(typeof ref);
    }

    function touchAll() {
        let keys = Object.keys(defaultValues);
        keys.forEach((key) => {
            touched[key] = true;
        });
        setTouched(touched);
    }

    function counterHit(currentError: CASASTestingErrors) {
        validationCount += 1;
        // we did not handle timeout problem, but it will in future
        if (validationCount === validationTotal) {
            validationCount = 0;
            if (_.isEmpty(currentError)) {
                setIsValid(true);
                if (isSubmit === true) {
                    isSubmit = false;
                    setIsLoading(true);
                    saveData({ ...values });
                } else {
                    setIsLoading(false);
                }
            } else {
                if (isSubmit === true) {
                    isSubmit = false;
                }
                if (isValid !== false) {
                    setIsValid(false);
                }
                setIsLoading(false);
            }


        }
    }

    function saveData(viewModel: CASASTestingValues) {
        setIsSubmitting(true);
        setIsLoading(true);
        setErrorMessage(null);
        let newData = _.cloneDeep(viewModel);
        newData.casastestingId = parseInt(TId);

        newData.firstname = newData.firstName;
        delete newData.firstName;

        newData.lastname = newData.lastName;
        delete newData.lastName;

        delete newData.confirmEmail;
        if(newData.testingCompletedDate){
            newData.testingCompletedDate = moment(newData.testingCompletedDate).format();
        }
        newData.registeredTime = null;

        saveCASASTesting(newData, false).then((res) => {
            setIsLoading(false);
            setIsSubmitting(false);
            if (res && res.status === 200) {
                setSubmitSuccess(true);
            } else if (res && res.status === 401) {
                setIsValid(false);

            }
            else {
                Swal.fire(
                    {
                        icon: 'error',
                        title: 'Error While Processing',
                        text: `${res && res.data ? res.data : "No detail reason available."}`,
                        showConfirmButton: true,
                        confirmButtonText: "OK",
                        confirmButtonColor: '#1678c2'
                    }
                ).then(() => {
                });
            }
        }, (error) => {
            setIsLoading(false);
            setIsSubmitting(false);
            if (error.response && error.response.data && error.response.status === 500) {
                let footer = '';
                Swal.fire(
                    {
                        icon: 'error',
                        title: 'Error While Processing',
                        text: `${error.response.data ? error.response.data : "No detail reason available"}`,
                        showConfirmButton: true,
                        footer: footer,
                        confirmButtonText: "OK",
                    }
                ).then(() => {
                });
            }
        });
    }

    function resetForm() {
        Object.keys(defaultValues).forEach((key, index) => {
            let temp = ref.current[key].current;
            if (temp != null) {
                // not implemented yet
            }
        });
        setValues(defaultValues);
        setIsLoading(false);
        setTouched({});
        setErrors({});
    }

    async function handleSubmit() {
        touchAll();
        setIsLoading(true);
        validateForm(values, validations, null);
        isSubmit = true;
    }

    function validateForm(values: CASASTestingValues, validations: ValidtionFunctions, fieldName: string | null = null) {
        let newErrors: CASASTestingErrors = {};
        setIsLoading(true);
        if (fieldName != null) {
            // only validate the changing field.Keep current errors info
            newErrors = errors;
            validationTotal = 1;
            validationCount = 0;
            validations[fieldName](values).then(() => {
                delete newErrors[fieldName];
                setErrors(newErrors);
                counterHit(newErrors);
            }, (error: string) => {
                newErrors[fieldName] = error;
                setErrors(newErrors);
                counterHit(newErrors);
            });
        } else {
            // if this validate is not trigered by input control change, validate all.
            let keys = Object.keys(defaultValues);
            validationTotal = keys.length > 0 ? keys.length : 0;
            validationCount = 0;
            keys.forEach((key) => {
                validations[key](values).then(() => {
                    delete newErrors[key];
                    setErrors(newErrors);
                    counterHit(newErrors);
                }, (error: string) => {
                    newErrors[key] = error;
                    setErrors(newErrors);
                    counterHit(newErrors);
                });
            });

        }

    }


    function handleBlur(fieldName: string) {
        if (errorMessage != null) {
            setErrorMessage(null);
        }
        setTimeout(() => {
            setTouched((prevValue) => {
                return {
                    ...prevValue,
                    [fieldName]: true,
                }
            });
            validateForm(values, validations, null);
        }, 250);
    }

    function handleChange(newValue: any, fieldName: string, fieldOnly = true) {
        if (errorMessage != null) {
            setErrorMessage(null);
        }
        values[fieldName] = newValue;
        setValues(values);
        if (!(touched[fieldName] != null)) {
            setTouched({ ...touched, [fieldName]: true });
        }
        debouncedValidateForm(values, validations, fieldOnly ? fieldName : null);
    }

    function isValidEmail(testTarget: string) {
        let myReg = /\b[\w\.-]+@[\w\.-]+\.\w{2,4}\b/gi;
        return myReg.test(testTarget);
    }

    function isValidPhone(testTarget: string) {
        let myReg = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;
        return myReg.test(testTarget);
    }

    function _isValidDate(dateText: string): boolean {
        if (!(dateText && typeof dateText === 'string' && dateText.length > 0)) {
            return false;
        } else {
            let testResult = moment(dateText);
            if (!testResult.isValid()) {
                return false;
            }
            return true;
        }
    }


    return [values, masterData, errors, touched, isLoading, isSubmitting, isValid, submitSuccess, errorMessage, registerRef, handleChange, handleBlur, handleSubmit] as const;

}
